import type { SocialBet } from 'src/types/domain/SocialBet'
import { displayAmount, displayMoney, getCurrencyDecimals } from 'src/utils/displayAmount'
import { useDeviceInfo } from 'src/hooks/useDeviceInfo'
import { useGameStore, useGetCurrentLanguage } from 'src/store/game'
import { useSocialCurrencyOverride } from 'src/components/SocialCurrencyDebugPanel/useSocialCurrencyOverride'
import { formatSocialAmount, getSocialCurrencySymbol } from '../../utils/getSocialCurrencySymbol'
import { COMPACT_THRESHOLD, formatSocialCompact } from '../../utils/formatSocialCompact'
import { getEmojiByAmount } from '../../utils/getEmojiByAmount'
import { getSocialUsdRate } from '../../utils/getSocialUsdRate'
import { getTextSizeByAmount } from '../../utils/getTextSizeByAmount'

type Props = Readonly<{
  bet: SocialBet
}>

export function SocialBetItem({ bet }: Props) {
  const { isMobile } = useDeviceInfo()
  const currentLanguage = useGetCurrentLanguage()
  const currency = useGameStore((state) => state.config?.bet?.currency)
  const usdRate = useGameStore((state) => state.config?.usdRate)
  // Staging-only social-feed currency preview (SocialCurrencyDebugPanel);
  // null in production builds — see useSocialCurrencyOverride.
  const override = useSocialCurrencyOverride()
  const effCurrency = override?.currency ?? currency
  // NOTE the deliberate asymmetry: an ACTIVE override REPLACES the server
  // rate even when its usdRate is undefined (no `??` fallback), so the BTC
  // preset demonstrates the excluded behaviour ($ + USD values) instead of
  // borrowing the live rate.
  const effUsdRate = override ? override.usdRate : usdRate
  const { rate, converted } = getSocialUsdRate(effCurrency, effUsdRate)

  // SCALED for display... (legacy displayMoney when unconverted — identical
  // to today's output; see the Task 6 formatter rationale). Massive converted
  // amounts (GC/kVND-class) compact to m/b so they don't overflow the stream
  // item — converted path only, same guard as the panel formatters.
  const scaled = bet.betAmount * rate
  const amount =
    converted && scaled >= COMPACT_THRESHOLD
      ? formatSocialCompact(scaled)
      : converted
        ? displayAmount(scaled, true, getCurrencyDecimals(scaled, effCurrency ?? 'USD'))
        : displayMoney(scaled, true)

  // ...but BUCKETED on the USD figure, so the emoji/speed/font mean the same
  // thing in every currency. Feeding these the converted value makes every ZAR
  // bet top-bucket and collapses IDR/VND to a single constant.
  const symbol = getSocialCurrencySymbol(currentLanguage, effCurrency, converted)
  const emoji = getEmojiByAmount(bet.betAmount)
  const prefix = `${emoji} `

  return (
    <div
      className="social-bet-stream__bet-item"
      style={{ fontSize: getTextSizeByAmount(bet.betAmount, isMobile) }}
    >
      <strong>
        <span className="value">{`${prefix}${formatSocialAmount(symbol, amount)}`}</span>
      </strong>
    </div>
  )
}
